import { Box, Stack, Typography } from "@mui/material"
import { UserMeetings } from "../components/upcomingOrders/meetings/UserMeetings"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { UserPurchases } from "../components/upcomingOrders/purchases/UserPurchases"
import { UserTakeaway } from "../components/upcomingOrders/takeaway/UserTakeaway"
import { DatePeriod } from "../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { DashboardPeriodPicker } from "../components/upcomingOrders/DashboardPeriodPicker"
import { CanteenInfo } from "../components/canteen/CanteenInfo"
import { OffersSection } from "../components/canteen/offers/OffersSection"
import { NewsSection } from "../components/canteen/news/NewsSection"
import { TodayMenuSection } from "../components/canteen/menu/TodayMenuSection"
import { useState } from "react"
import { RoomFinderDrawer } from "../components/roomFinder/RoomFinderDrawer"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectUserId } from "../../../../login/loginSelectors"
import { CreateOrderType } from "../../../../../shared/components/createOrder/helpers"
import {
    FinalSuccessDialogContent
} from "../../../../../shared/components/createOrder/components/finalSuccessDialog/FinalSuccessDialogContent"
import { getUserDetails, UserQuery } from "../../../../users/userApi"
import { WalletSummaryCards } from "../components/wallets/WalletSummaryCards"
import { SubscriptionCards } from "../components/subscriptions/SubscriptionCards"
// Re-enable once the check-in/out reminders API is available (currently hardcoded data)
// import { CheckInReminders } from "../components/checkInReminders/CheckInReminders"
import { makeStyles } from "tss-react/mui"
import { CreateOrderDialogMode } from "../../../../../shared/components/createOrder/createOrderTypes"
import { useOrderDialogUrlState } from "../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { RefetchIntervals } from "../../../../../core/api/refetchIntervals"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"
import { ModuleName } from "../../../../users/userTypes"
import { fetchProperties, PropertiesQuery } from "../../../../properties/propertiesApi"
import { LocalCache, StorageKey } from "../../../../../shared/cache/LocalCache"
import { FindRoom } from "../components/canteen/FindRoom"

function toDatePeriod(value: string | null): DatePeriod | undefined {
    return Object.values(DatePeriod).find((period) => period === value)
}

const useStyles = makeStyles()((theme) => ({
    container: {
        paddingTop: "3rem",
    },
    columns: {
        display: "grid",
        gap: "2rem",
        gridTemplateColumns: "minmax(0, 1fr)",
        [theme.breakpoints.up("md")]: {
            gap: "5rem",
            gridTemplateColumns: "minmax(0, 704fr) minmax(0, 540fr)",
        },
    },
    summaryCardsRow: {
        display: "flex",
        flexDirection: "row",
        gap: "1rem",
        overflowX: "auto",
    },
    sectionTitle: {
        fontSize: "1.25rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        marginBottom: ".5rem",
    },
}))

export function UserReportsHome() {
    const { classes } = useStyles()
    const userId = useAppSelector(selectUserId)
    const [isRoomFinderOpen, setIsRoomFinderOpen] = useState(false)
    const [datePeriod, setDatePeriod] = useState<DatePeriod>(
        () => toDatePeriod(LocalCache().get(StorageKey.USER_DASHBOARD_TIME_PERIOD)) ?? DatePeriod.TODAY,
    )

    function handleDatePeriodChange(period: DatePeriod) {
        setDatePeriod(period)
        LocalCache().set(StorageKey.USER_DASHBOARD_TIME_PERIOD, period)
    }
    const { openDialog } = useOrderDialogUrlState()

    const [finalSuccessDialog, setFinalSuccessDialog] = useState(false)
    const [orderId, setOrderId] = useState<number>()

    const { data: userDetails } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
        enabled: Boolean(userId),
        refetchInterval: RefetchIntervals.medium,
    })

    const activeModules = new Set(userDetails?.user?.modules?.filter((m) => m.isActive)?.map((m) => m.name))
    const hasRefillWallet = activeModules.has(ModuleName.REFILL_WALLET)
    const hasCatering = activeModules.has(ModuleName.CATERING)
    const hasTakeAway = activeModules.has(ModuleName.TAKE_AWAY)

    const { data: properties } = useApiQuery(fetchProperties, {
        queryName: PropertiesQuery.PROPERTIES,
        dependencies: {},
    })
    const showLunch = properties?.showLunchModule ?? false
    const showLunchSubscription = properties?.showLunchSubscriptionModule ?? false

    const hasAnyAccountSection = hasRefillWallet || showLunchSubscription
    const hasAnyUpcomingOrder = hasTakeAway || showLunch || hasCatering

    const handleOpenSuccessDialog = (orderId: number) => {
        setOrderId(orderId)
        setFinalSuccessDialog(true)
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.columns}>
                <Stack direction="column" spacing="3.75rem">
                    {/* Enable once the check-in/out reminders API is available (currently hardcoded data) */}
                    {/* <CheckInReminders /> */}
                    {hasAnyAccountSection && (
                        <Stack direction="column" spacing="1.5rem">
                            {hasRefillWallet && <WalletSummaryCards />}
                            {showLunchSubscription && <SubscriptionCards />}
                        </Stack>
                    )}
                    {hasAnyUpcomingOrder && (
                        <Stack direction="column" spacing="1rem">
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem">
                                <LocalizedStrict id="user-home-upcoming-orders">
                                    <Typography variant="h2" color="text.primary" fontSize="3rem" fontWeight="bold">
                                        Upcoming orders
                                    </Typography>
                                </LocalizedStrict>
                                <DashboardPeriodPicker datePeriod={datePeriod} onChange={handleDatePeriodChange} />
                            </Stack>
                            <Stack direction="column" spacing="1.875rem">
                                {hasTakeAway && <UserTakeaway period={datePeriod} />}
                                {showLunch && <UserPurchases period={datePeriod} />}
                                {hasCatering && <UserMeetings period={datePeriod} />}
                            </Stack>
                        </Stack>
                    )}
                </Stack>

                <Box>
                    <CanteenInfo />
                    <MatchEnvironment
                        environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}
                    >
                        {activeModules.has(ModuleName.BOOKING) && (
                            <FindRoom
                                onOpenRoomFinder={() => {
                                    setIsRoomFinderOpen(true)
                                }}
                            />
                        )}
                    </MatchEnvironment>
                    <OffersSection />
                    <TodayMenuSection />
                    <NewsSection />
                </Box>
            </Box>

            <RoomFinderDrawer
                open={isRoomFinderOpen}
                openSuccessDialog={handleOpenSuccessDialog}
                onClose={() => setIsRoomFinderOpen(false)}
            />

            {finalSuccessDialog && (
                <FinalSuccessDialogContent
                    open={finalSuccessDialog}
                    onClose={() => setFinalSuccessDialog(false)}
                    handleNewOrder={() => {
                        setIsRoomFinderOpen(true)
                        setFinalSuccessDialog(false)
                    }}
                    handleAddMeetingService={() => {
                        openDialog({
                            orderId: orderId,
                            mode: CreateOrderDialogMode.EDIT,
                            activeOrderTab: CreateOrderType.MEETING_SERVICE,
                        })
                        setFinalSuccessDialog(false)
                    }}
                    handleFinish={() => setFinalSuccessDialog(false)}
                />
            )}
        </Box>
    )
}
